import type { ReactNode } from "react";

import { BrandPortalModal, Button } from "../../../../components/design-system";
import type {
  Audience,
  BudgetState,
} from "../../../../pages/Ads2DControlsAlphaBeta/shared";
import { COPY } from "../copy";
import { getP8Profile } from "../p8AdvertiserData";

import { PreferredStoreTypePill } from "./PreferredStoreTypePill";
import { SettingsModalFooter } from "./SettingsModalFooter";

type ConfirmAdvertisingSettingsModalProps = {
  open: boolean;
  growthModeLabel: string;
  budget: BudgetState;
  audience: Audience;
  selectedStoreTypes: string[];
  newProductsBoosting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

function formatDollars(value: string): string {
  const amount = Number.parseInt(value, 10) || 0;
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function getBudgetSummary(budget: BudgetState): {
  display: string;
  description: string;
} {
  const profile = getP8Profile();
  const renewDate = profile.budget.nextRenewsAtDate
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(new Date(profile.budget.nextRenewsAtDate))
    : "Aug 1, 2026";
  const prorateMonth = new Intl.DateTimeFormat("en-US", {
    month: "long",
  }).format(new Date(2026, 6, 1));
  const description = `Your budget will be prorated for ${prorateMonth} and will automatically renew on a recurring monthly basis starting ${renewDate}.`;

  if (budget.separateBudget) {
    const total =
      (Number.parseInt(budget.newCustomerBudget, 10) || 0) +
      (Number.parseInt(budget.returningBudget, 10) || 0);
    return {
      display: `${formatDollars(String(total))} (${formatDollars(budget.newCustomerBudget)} for new, ${formatDollars(budget.returningBudget)} for returning)`,
      description,
    };
  }

  return {
    display: formatDollars(budget.overallBudget),
    description,
  };
}

/**
 * Read straight off the panel's own option titles. This dialog is the readback
 * of the choice made one click earlier, so anything but the same two strings
 * would have the reader confirming a setting under a name they haven't seen.
 */
function getAudienceLabel(audience: Audience): string {
  return audience === "both"
    ? COPY.settings.adType.both.title
    : COPY.settings.adType.new.title;
}

function SummaryRow({
  label,
  value,
  description,
  children,
}: {
  label: string;
  value?: string;
  description?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 border-b border-[#dfe0e1] pb-4">
      <p className="text-sm text-[#757575]">{label}</p>
      {value ? (
        <p className="text-sm font-medium text-[#333]">{value}</p>
      ) : null}
      {description ? (
        <p className="text-xs leading-4 text-[#333]">{description}</p>
      ) : null}
      {children}
    </div>
  );
}

export function ConfirmAdvertisingSettingsModal({
  open,
  growthModeLabel,
  budget,
  audience,
  selectedStoreTypes,
  newProductsBoosting,
  onCancel,
  onConfirm,
}: ConfirmAdvertisingSettingsModalProps) {
  const budgetSummary = getBudgetSummary(budget);

  return (
    <BrandPortalModal
      open={open}
      onClose={onCancel}
      title="Confirm advertising settings"
      width="648px"
      footerClassName="!pt-4"
      contextualInfo={
        <p className="text-xs leading-4 text-[#333]">
          By clicking confirm settings, I agree to the Promoted Listings{" "}
          <button type="button" className="underline">
            Terms &amp; Conditions
          </button>
          .
        </p>
      }
      footer={
        <SettingsModalFooter
          actions={
            <>
              <Button variant="secondary" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="primary" size="small" onClick={onConfirm}>
                Confirm settings
              </Button>
            </>
          }
        />
      }
    >
      <div className="flex flex-col gap-6">
        <p className="text-sm leading-5 text-[#333]">
          Your new advertising settings will take effect within 24 hours. For
          best results, we recommend running your campaign for a month before
          evaluating changes in performance.
        </p>

        <div className="flex flex-col gap-4">
          <SummaryRow label="Growth mode" value={growthModeLabel} />
          <SummaryRow
            label="Monthly budget"
            value={budgetSummary.display}
            description={budgetSummary.description}
          />
          <SummaryRow
            label={COPY.settings.adType.heading}
            value={getAudienceLabel(audience)}
          />
          {selectedStoreTypes.length > 0 ? (
            <SummaryRow label="Preferred store types">
              <div className="flex flex-wrap gap-2">
                {selectedStoreTypes.map((storeType) => (
                  <PreferredStoreTypePill key={storeType} label={storeType} />
                ))}
              </div>
            </SummaryRow>
          ) : null}
          <SummaryRow
            label="New product boosting"
            value={newProductsBoosting ? "On" : "Off"}
          />
        </div>
      </div>
    </BrandPortalModal>
  );
}
